import { UseCourse, UseInstructor, UseRoom } from '@core/usecases';
import { FormService, Logger } from '@core/services';
import { CourseEntity, CourseUser, InstructorEntity, RoomEntity, UserEntity } from '@core/entities';
import { ChangeDetectorRef, inject } from '@angular/core';
import { RRule } from 'rrule';
import { DateTimeUtility, getAlertTypeByApprovedStatusOfEntity, GetUserId, isMobileView, scrollToTop } from '@core/utils';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { TranslateService } from '@ngx-translate/core';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { untilDestroyed } from '@ngneat/until-destroy';
import { ConfirmationDialogType, CourseInstructorType, DeleteConfirmationOptions, NotificationType, SocketEvents } from '@core/enums';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';
import { HotToastService } from '@ngxpert/hot-toast';
import { Router } from '@angular/router';
import { SocketIoService } from '@core/socket-io';
import { ButtonComponent } from '@shared/components/ui';
import { saveAs } from 'file-saver';
import { CourseFormComponent } from '@shared/components/forms/course-form/course-form.component';
import { PermissionService } from '@auth';
import { CalendarEventItem } from '@core/types';

const log = new Logger('Courses Composition');

export class CoursesComposition extends FormService {
  courses: CourseEntity[] = [];
  allRooms: RoomEntity[] = [];
  coursesTabs: any[] = [];
  paginatedCourses: CourseEntity[] = null;

  currentTab = 'today';

  filterQuery: any = {
    tags: '',
    instructorType: null,
    room: 0,
  };

  // ---- For Course Details Dialog ----

  course: CourseEntity;
  instructor: InstructorEntity;
  unApprovedCourseUsers: UserEntity[] = [];
  approvedCourseUsers: CourseUser[] = [];
  room: RoomEntity;
  currentDate: string;

  // ----------------------------------

  // --- Specific Properties for Calendar View ---
  isCalendarView = false;
  unFilteredCoursesForCalendarView: CourseEntity[] = [];
  calendarEvents: CalendarEventItem[] = [];
  appliedFilters: any = {
    year: null,
    month: null,
  };
  // --- end Specific Properties for Calendar View ---

  // Recurring Courses properties
  upComingCourses: CourseEntity[] = [];
  todayCourses: CourseEntity[] = [];
  protected _dialogService: DialogService;
  protected _dialogRef: DialogRef;
  protected _bottomSheetService: NgxBottomSheetService;

  protected readonly _translateService = inject(TranslateService);
  protected readonly _socketService = inject(SocketIoService);
  protected readonly _router = inject(Router);

  private _allClubUsers: UserEntity[] = [];
  private _originalCourses: CourseEntity[] = [];
  private _recurringCourses: CourseEntity[] = [];

  private readonly _useCourse = new UseCourse();
  private readonly _useInstructor = new UseInstructor();
  private readonly _useRoom = new UseRoom();
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _toast = inject(HotToastService);
  private readonly _permissionService = inject(PermissionService);

  constructor() {
    super();

    this._bottomSheetService = inject(NgxBottomSheetService);
    this._dialogService = inject(DialogService);

    // this._initDataForForm();

    this.coursesTabs = [
      { label: 'today', displayName: this._translateService.instant('courses.today'), count: 0 },
      { label: 'upcoming', displayName: this._translateService.instant('courses.upcoming'), count: 0 },
    ];

    this._socketService
      .fromEvent(SocketEvents.GetNotification)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data: NotificationType) => {
          if (data === NotificationType.Course) {
            log.info('Updating courses list');
            this._reloadCurrentRoute();
            this._fetchAllCourses();
          }
        },
      });
  }

  set allClubUsers(users: UserEntity[]) {
    this._allClubUsers = users;
    if (this._originalCourses.length > 0) {
      this._originalCourses.forEach((course) => {
        course.courseUsers.forEach((courseUser) => {
          if (courseUser && courseUser.users) {
            courseUser.users.memberId = +this._allClubUsers.find((user) => user.id === courseUser.userId).memberId;
          }
        });
      });
      this._cdr.detectChanges();
    }
  }

  get instructorType(): string {
    return this.course.instructorType === CourseInstructorType.Internal ? 'Internal Instructor' : 'External Instructor';
  }

  get isUserAdmin(): boolean {
    return this._permissionService.isAdmin();
  }

  get isUserAuthor(): boolean {
    return +this.course?.author === +GetUserId();
  }

  get availabilityCount(): number {
    // return this.course.courseUsers.filter((value) => value.approvedStatus === ApprovedStatus.Approved).length;
    return this.course.courseUsers.length;
  }

  get isCourseFullyBooked(): boolean {
    return this.availabilityCount >= this.course.allowedPersons;
  }

  get approvedFilteredCourseUsers() {
    return this.approvedCourseUsers?.filter((user) => user.id !== this.course?.author && user.users);
  }

  get alertType() {
    return getAlertTypeByApprovedStatusOfEntity(this.course);
  }

  onTabChange(tabName: string) {
    this.currentTab = tabName;
    this.paginatedCourses = null;
    switch (tabName) {
      case 'today':
        this.courses = this.todayCourses;
        break;
      case 'upcoming':
        this.courses = this.upComingCourses;
        break;
      default:
        this.courses = this._originalCourses;
    }
    this._cdr.detectChanges();
  }

  updatePaginatedCourses(courses: CourseEntity[]) {
    this.paginatedCourses = courses as CourseEntity[];
    scrollToTop('.courses-wrapper');
    this._cdr.detectChanges();
  }

  // Adjusting the filtering function
  filterCourses() {
    let filteredCourses = this._originalCourses;

    // Apply Instructor Type filter if not null
    if (this.filterQuery.instructorType !== null) {
      const instructorType = parseInt(this.filterQuery.instructorType); // Parse string to number
      filteredCourses = filteredCourses.filter((course) => {
        return course.instructorType.toString() === instructorType.toString();
      });
    }

    // Apply Tags filter if array is not empty
    if (this.filterQuery.tags && this.filterQuery.tags.length > 0) {
      filteredCourses = filteredCourses.filter((course) => {
        return course.courseTags && course.courseTags.some((tag) => this.filterQuery.tags.includes(tag.tagName));
      });
    }

    // Apply Room filter if array is not empty
    if (this.filterQuery.room && this.filterQuery.room.length > 0) {
      const roomIds = this.filterQuery.room.map((room: RoomEntity) => room.id);
      filteredCourses = filteredCourses.filter((course) => {
        return roomIds.includes(course.room);
      });
    }

    this.courses = this._processCourses(filteredCourses, false);

    this.calendarEvents = this._transformCoursesToCalendarEvents(this.courses);

    this._updateCourseTabs();
    this.onTabChange(this.currentTab);
  }

  deleteCourse(course: CourseEntity, btnRef?: ButtonComponent) {
    if (!course.id) return;

    btnRef?.showLoading();

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: course.name,
        },
      });

      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this.call(this._useCourse.deleteCourse(course.id), btnRef).then(() => {
                  this._toast.success(this._translateService.instant('Delete Successful'));
                  this._bottomSheetService.closeAll();
                  this._socketService.emit(SocketEvents.SendNotification, NotificationType.Course);
                  // this._reloadCurrentRoute();
                });
              }
              break;
            default:
              btnRef?.showIdle();
              break;
          }
        },
      });
    } else {
      const confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.DELETE,
          emphasize: course.name,
        },
      });
      confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (result: any) => {
          if (result) {
            this.call(this._useCourse.deleteCourse(course.id), btnRef).then(() => {
              this._toast.success(this._translateService.instant('Delete Successful'));
              this._dialogService.closeAll();
              this._socketService.emit(SocketEvents.SendNotification, NotificationType.Course);
              // this._reloadCurrentRoute();
            });
          }
          btnRef?.showIdle();
        },
      });
    }
  }

  editCourse(course: CourseEntity) {
    if (!course.id) return;

    if (isMobileView()) {
      this._bottomSheetService.open(CourseFormComponent, {
        showCloseButton: false,
        data: {
          course,
        },
      });
    } else {
      this._dialogService.open(CourseFormComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-y-auto',
        data: {
          course,
        },
      });
    }
  }

  joinCourse(course: CourseEntity) {
    // confirm dialog
    this._dialogService
      .open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.JOIN,
          emphasize: course.name,
        },
      })
      .afterClosed$.pipe(untilDestroyed(this))
      .subscribe({
        next: (result: any) => {
          if (result) {
            this._useCourse
              .joinCourse(course.id)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: () => {
                  this._toast.success(this._translateService.instant('Join Successful'));
                  this._socketService.emit(SocketEvents.SendNotification, NotificationType.Course);
                  this._reloadCurrentRoute();
                },
              });
          }
        },
      });
  }

  acceptCourse(btnRef?: ButtonComponent) {
    if (!this.course || !this.course.id) return;
    btnRef?.showLoading();

    this._dialogService
      .open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.Accept,
          emphasize: this.course.name,
        },
      })
      .afterClosed$.pipe(untilDestroyed(this))
      .subscribe({
        next: (result: any) => {
          if (result) {
            this._useCourse
              .acceptCourse(this.course.id)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: (res) => {
                  this._toast.success(this._translateService.instant('Accept Successful'));
                  this._socketService.emit(SocketEvents.SendNotification, NotificationType.Course);
                  btnRef?.showIdle();
                },
                error: (err) => {
                  this._toast.error(err.error.messgae);
                  btnRef?.showError();
                },
              });
          } else {
            btnRef?.showIdle();
          }
        },
        error: (err) => {
          btnRef?.showIdle();
        },
      });
  }

  denyCourse(btnRef?: ButtonComponent) {
    if (!this.course || !this.course.id) return;
    btnRef?.showLoading();

    this._dialogService
      .open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.Deny,
          emphasize: this.course.name,
        },
      })
      .afterClosed$.pipe(untilDestroyed(this))
      .subscribe({
        next: (result: any) => {
          if (result) {
            this._useCourse
              .denyCourse(this.course.id)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: (res) => {
                  this._toast.success(this._translateService.instant('Deny Successful'));
                  this._socketService.emit(SocketEvents.SendNotification, NotificationType.Course);
                  btnRef?.showIdle();
                },
                error: (err) => {
                  this._toast.error(err.error.messgae);
                  btnRef?.showError();
                },
              });
          } else {
            btnRef?.showIdle();
          }
        },
        error: (err) => {
          btnRef?.showIdle();
        },
      });
  }

  close() {
    if (this._dialogRef) {
      this._dialogRef.close();
    } else {
      this._bottomSheetService.closeAll();
    }
  }

  _fetchAllCourses() {
    this.call(this._useCourse.getAllCourses())
      .then((res: CourseEntity[]) => {
        if (res) {
          this._originalCourses = res;
          this._originalCourses.forEach((course) => {
            if (course.courseUsers && course.courseUsers.length) {
              course.courseUsers = course.courseUsers.filter((courseUser) => courseUser.users);
              course.courseUsers?.forEach((courseUser) => {
                if (courseUser && courseUser.users) {
                  courseUser.users.memberId = +this._allClubUsers?.find((user) => user.id === courseUser.userId)?.memberId || null;
                }
              });
            }
          });

          this._recurringCourses = this._processCourses(res, false);
          this.courses = this._recurringCourses;
          this.calendarEvents = this._transformCoursesToCalendarEvents(this.courses);

          this._cdr.detectChanges();
        } else {
          log.info('No Courses found');
        }
      })
      .catch((err) => {
        log.error('Error while fetching Courses', err);
      });
  }

  async downloadDocument(course: CourseEntity, btnRef?: ButtonComponent) {
    if (course.document) {
      btnRef?.showLoading();

      try {
        const filePath = course.document;
        const fileName = filePath.split('/').pop();

        const response = await this._useCourse.downloadDocument(course.id, filePath).toPromise();
        const blob = new Blob([response], { type: 'application/octet-stream' }); // Continue using a generic MIME type

        // Use FileSaver to save the file
        saveAs(blob, fileName || 'course-document');

        btnRef?.showIdle();
      } catch (error) {
        log.error('Error downloading the document:', error);
        btnRef?.showError();
      }
    } else {
      log.error('Course document URL or title is missing.');
      btnRef?.showError();
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  // --- For Course Details Dialog ---

  protected _fetchAllCourseDetails(courseId: number) {
    this._fetchCourseDetails(courseId);
    this._fetchUnapprovedCourseUsers(courseId);
    this._fetchApprovedCourseUsers(courseId);
  }

  protected _fetchCourseDetails(courseId: number) {
    this.call(this._useCourse.getCourseById(courseId))
      .then((res: CourseEntity) => {
        if (res) {
          this.course = res;
          this._cdr.detectChanges();
          log.info('Course Details fetched successfully');

          const instructorId =
            this.course.courseInternalInstructor && this.course.courseInternalInstructor.length > 0
              ? this.course.courseInternalInstructor[0].instructorId
              : this.course.courseExternalInstructor[0].instructorId;
          this._fetchInstructorDetails(instructorId);

          this.course.currentDate = this.currentDate;

          this._fetchRoomDetails(+this.course.room);
        } else {
          log.info('No Course found');
        }
      })
      .catch((err) => {
        log.error('Error while fetching Course Details', err);
      });
  }

  private _fetchUnapprovedCourseUsers(courseId: number) {
    this._useCourse
      .getUnapprovedParticipants(courseId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: UserEntity[]) => {
          if (res) {
            this.unApprovedCourseUsers = res;
            this._cdr.detectChanges();
            log.info('Unapproved Course Users fetched successfully');
          } else {
            log.info('No Unapproved Course Users found');
          }
        },
      });
  }

  private _fetchApprovedCourseUsers(courseId: number) {
    this._useCourse
      .getApprovedParticipants(courseId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: CourseUser[]) => {
          if (res) {
            this.approvedCourseUsers = res;
            this._cdr.detectChanges();
          } else {
            log.info('No Approved Course Users found');
          }
        },
      });
  }

  private _fetchInstructorDetails(instructorId: number) {
    if (!instructorId) return;
    this._useInstructor
      .getInstructorById(instructorId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: InstructorEntity) => {
          if (res) {
            this.instructor = res;
            this._cdr.detectChanges();
          } else {
            log.info('No Instructor found');
          }
        },
      });
  }

  private _fetchRoomDetails(roomId: number) {
    if (!roomId) return;
    this._useRoom
      .getRoomById(roomId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: RoomEntity) => {
          if (res) {
            this.room = res;
            this._cdr.detectChanges();
          } else {
            log.info('No Room found');
          }
        },
      });
  }

  // ---------------------------------

  private _processCourses(courses: CourseEntity[], filterPastCourses = false): CourseEntity[] {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const allCourses: CourseEntity[] = [];

    // Clear previous entries in today and upcoming courses to prevent old data display
    this.todayCourses = [];
    this.upComingCourses = [];

    courses.forEach((course) => {
      if (course.recurrence && course.recurrence !== 'null' && course.recurrence !== 'undefined' && course.recurrence !== '') {
        const rruleSet = RRule.fromString(course.recurrence);
        const startDates = rruleSet.all();
        startDates.forEach((startDate) => {
          const startDateUtc = new Date(startDate.toISOString());
          const startDateIso = startDateUtc.toISOString().split('T')[0];

          if (!filterPastCourses || startDateUtc >= now) {
            const courseClone = this._cloneAndAdjustCourse(course, startDateUtc);
            allCourses.push(courseClone);
            this._categorizeCourse(courseClone, startDateIso, today);
          }
        });
      } else {
        this._handleNonRecurringCourse(course, now, today, allCourses);
      }
    });

    this._sortCourses(allCourses);
    this._sortCourses(this.todayCourses, true);
    this._sortCourses(this.upComingCourses, true);
    this._updateCourseTabs();
    return allCourses;
  }

  private _categorizeCourse(course: CourseEntity, startDateIso: string, today: string) {
    if (startDateIso === today) {
      this.todayCourses.push(course);
    } else if (startDateIso > today) {
      this.upComingCourses.push(course);
    }
  }

  private _handleNonRecurringCourse(course: CourseEntity, now: Date, today: string, allCourses: CourseEntity[], filterPastCourses = false) {
    const courseStartDateTime = new Date(course.dateFrom);
    const courseStartDateIso = courseStartDateTime.toISOString().split('T')[0];

    if (!filterPastCourses || courseStartDateTime >= now) {
      course.currentDate = courseStartDateIso;
      allCourses.push(course);
      this._categorizeCourse(course, courseStartDateIso, today);
    }
  }

  private _updateCourseTabs(): void {
    this.coursesTabs = [
      { label: 'today', displayName: this._translateService.instant('courses.today'), count: this.todayCourses.length },
      {
        label: 'upcoming',
        displayName: this._translateService.instant('courses.upcoming'),
        count: this.upComingCourses.length,
      },
    ];
  }

  private _cloneAndAdjustCourse(course: CourseEntity, startDate: Date): CourseEntity {
    const startTime = course.recurringDates?.[0]?.startTime || '00:00';
    const endTime = course.recurringDates?.[0]?.endTime || '00:00';
    const courseClone = { ...course };

    courseClone.dateFrom = this._combineDateTimeUTC(startDate, startTime);
    courseClone.dateTo = this._combineDateTimeUTC(startDate, endTime);

    courseClone.currentDate = startDate.toISOString().split('T')[0]; // Adjusted date in UTC

    courseClone.startTime = startTime;
    courseClone.endTime = endTime;

    return courseClone as CourseEntity;
  }

  private _sortCourses(courses: CourseEntity[], sortByTime = false): void {
    courses.sort((a, b) => {
      const dateComparison = a.currentDate.localeCompare(b.currentDate);
      if (dateComparison !== 0) return dateComparison;

      if (sortByTime) {
        return (a.startTime || '').localeCompare(b.startTime || '');
      }

      return 0;
    });
  }

  private _combineDateTimeUTC(date: Date, time: string): string {
    const [hours, minutes] = time.split(':');
    date.setUTCHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return date.toISOString();
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }

  private _transformCoursesToCalendarEvents(courses: CourseEntity[]): CalendarEventItem[] {
    return courses.map((course) => {
      const calendarEvent: CalendarEventItem<CourseEntity> = {
        id: course.id,
        title: course.name,
        date: course.currentDate,
        startTime: DateTimeUtility.createDateTime(course.currentDate, course.startTime).toISOString(),
        endTime: DateTimeUtility.createDateTime(course.currentDate, course.endTime).toISOString(),
        meta: course,
      };
      return calendarEvent;
    });
  }
}
